import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
  Card,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const EmployeeFeedbackForm = () => {
  const [loading, setLoading] = useState(false);
  const [fetchingDraft, setFetchingDraft] = useState(true);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [employeeId, setEmployeeId] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [hasExistingDraft, setHasExistingDraft] = useState(false);
  const [hasSubmittedFeedback, setHasSubmittedFeedback] = useState(false);
  const [showDraftDialog, setShowDraftDialog] = useState(false);
  const [draftToLoad, setDraftToLoad] = useState(null);
  const [currentQuarter, setCurrentQuarter] = useState('');

  const [formData, setFormData] = useState({
    engagementScore: '',
    workLifeBalance: '',
    managerSatificationScore: '',
    managementSupport: '',
    careerGrowth: '',
    compensation: '',
    workEnvironment: '',
    teamCollaboration: '',
    learningDevelopment: '',
    recognition: '',
    overallSatisfaction: '',
    likeMost: '',
    improvements: '',
    additionalComments: '',
    wouldRecommend: '',
    feedbackPeriod: ''
  });

  // Function to get current quarter
  const getCurrentQuarter = () => {
    const currentDate = new Date();
    const month = currentDate.getMonth();
    const quarter = Math.floor(month / 3) + 1;
    const year = currentDate.getFullYear();
    const quarterStr = `Q${quarter} ${year}`;
    setCurrentQuarter(quarterStr);
    return quarterStr;
  };

  // Fetch user data and check for existing drafts/submissions
  useEffect(() => {
    const fetchUserAndFeedbackStatus = async () => {
      try {
        setFetchingDraft(true);
        
        // Step 1: Get current user from session
        const userResponse = await axios.get(`${API_URL}/api/user/current`, { 
          withCredentials: true 
        });
        
        const employeeIdFromSession = userResponse.data.employee_id;
        
        if (!employeeIdFromSession) {
          setError("Employee ID not found. Please log in again.");
          setFetchingDraft(false);
          return;
        }

        setEmployeeId(employeeIdFromSession);

        // Step 2: Get token for company_id
        const token = localStorage.getItem("token");
        if (!token) {
          setError("No authentication token found. Please log in.");
          setFetchingDraft(false);
          return;
        }

        const payload = JSON.parse(atob(token.split('.')[1]));
        const companyIdFromToken = payload.company_id || payload.registration_no;
        
        if (!companyIdFromToken) {
          setError("Company ID not found. Please contact HR.");
          setFetchingDraft(false);
          return;
        }
        
        setCompanyId(companyIdFromToken);

        // Step 3: Verify employee exists
        const employeeResponse = await axios.get(
          `${API_URL}/api/employees/${employeeIdFromSession}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (employeeResponse.data.success) {
          
          // Step 4: Check feedback status for current quarter
          const currentQuarterStr = getCurrentQuarter();
          await checkFeedbackStatus(
            employeeIdFromSession, 
            companyIdFromToken, 
            token, 
            currentQuarterStr
          );
        }
      } catch (err) {
        //console.error("Error fetching user or feedback status:", err);
        setError(err.response?.data?.message || "Failed to load user information.");
      } finally {
        setFetchingDraft(false);
      }
    };

    fetchUserAndFeedbackStatus();
  }, []);

  // Function to check feedback status (drafts and submissions)
  const checkFeedbackStatus = async (employeeId, companyId, token, currentQuarter) => {
    try {
      
      const response = await axios.get(
        `${API_URL}/api/empfeedback/my-feedback`,
        {
          headers: { Authorization: `Bearer ${token}` },
         
        }
      );

      if (response.data.success && response.data.data) {
        const allFeedbacks = response.data.data;
        
        // Check for submitted feedback for current quarter
        const submittedFeedback = allFeedbacks.filter(feedback => 
          feedback.status === 'submitted' && 
          feedback.feedbackPeriod === currentQuarter
        );

        if (submittedFeedback.length > 0) {
          setHasSubmittedFeedback(true);
          
          // Set feedback period and disable form
          setFormData(prev => ({
            ...prev,
            feedbackPeriod: currentQuarter
          }));
          
          return;
        }

        // If not submitted, check for drafts for current quarter
        const currentDrafts = allFeedbacks.filter(feedback => 
          feedback.status === 'draft' && 
          feedback.feedbackPeriod === currentQuarter
        );

        if (currentDrafts.length > 0) {
          
          // Store the latest draft
          const latestDraft = currentDrafts.reduce((latest, current) => {
            return new Date(current.updatedAt) > new Date(latest.updatedAt) ? current : latest;
          });

          setDraftToLoad(latestDraft);
          setHasExistingDraft(true);
          setShowDraftDialog(true);
        } else {
          // No draft for current period, set default period
          setFormData(prev => ({
            ...prev,
            feedbackPeriod: currentQuarter
          }));
        }
      }
    } catch (err) {
      //console.error('Error checking feedback status:', err);
      // Set default period even if check fails
      setFormData(prev => ({
        ...prev,
        feedbackPeriod: currentQuarter
      }));
    }
  };

  // Load draft into form
  const loadDraft = () => {
    if (draftToLoad) {
      const draftData = {
        engagementScore: draftToLoad.engagementScore || '',
        workLifeBalance: draftToLoad.workLifeBalance || '',
        managerSatificationScore: draftToLoad.managerSatificationScore || '',
        managementSupport: draftToLoad.managementSupport || '',
        careerGrowth: draftToLoad.careerGrowth || '',
        compensation: draftToLoad.compensation || '',
        workEnvironment: draftToLoad.workEnvironment || '',
        teamCollaboration: draftToLoad.teamCollaboration || '',
        learningDevelopment: draftToLoad.learningDevelopment || '',
        recognition: draftToLoad.recognition || '',
        overallSatisfaction: draftToLoad.overallSatisfaction || '',
        likeMost: draftToLoad.likeMost || '',
        improvements: draftToLoad.improvements || '',
        additionalComments: draftToLoad.additionalComments || '',
        wouldRecommend: draftToLoad.wouldRecommend || '',
        feedbackPeriod: draftToLoad.feedbackPeriod || currentQuarter
      };
      
      setFormData(draftData);
      setSuccess('Draft loaded successfully!');
      setTimeout(() => setSuccess(''), 3000);
    }
    setShowDraftDialog(false);
  };

  // Discard draft and start fresh
  const discardDraft = () => {
    setFormData(prev => ({
      ...prev,
      feedbackPeriod: currentQuarter
    }));
    
    setShowDraftDialog(false);
    setHasExistingDraft(false);
  };

  const handleRadioChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleTextChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSaveDraft = async () => {
    if (!employeeId || !companyId) {
      setError("User data not loaded. Please refresh the page.");
      return;
    }

    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem("token");
      
      // Ensure feedback period is set
      if (!formData.feedbackPeriod) {
        formData.feedbackPeriod = currentQuarter;
      }
      
      await axios.post(
        `${API_URL}/api/empfeedback/draft`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );

      setSuccess('Draft saved successfully!');
      setHasExistingDraft(true);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      //console.error('Save draft error:', err);
      setError(err.response?.data?.message || 'Failed to save draft');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    if (!employeeId || !companyId) {
      setError("User data not loaded. Please refresh the page.");
      return;
    }
  
    if (hasSubmittedFeedback) {
      setError(`You have already submitted feedback for ${currentQuarter}.`);
      return;
    }
  
    // Validate required text fields
    if (!formData.likeMost.trim()) {
      setError('Please tell us what you like most about working here');
      return;
    }
  
    if (!formData.improvements.trim()) {
      setError('Please tell us what can be improved');
      return;
    }

    // Validate all rating fields
    const ratingFields = [
      'engagementScore', 'workLifeBalance', 'managementSupport', 'managerSatificationScore',
      'careerGrowth', 'compensation', 'workEnvironment', 
      'teamCollaboration', 'learningDevelopment', 'recognition', 
      'overallSatisfaction'
    ];

    for (const field of ratingFields) {
      if (!formData[field]) {
        setError(`Please provide a rating for all questions`);
        return;
      }
    }

    if (!formData.wouldRecommend) {
      setError('Please answer if you would recommend this organization');
      return;
    }
  
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem("token");
  
      const response = await axios.post(
        `${API_URL}/api/empfeedback/submit`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`
          },
          withCredentials: true
        }
      );
  
      if (response.data.success) {
        setSuccess(`Feedback submitted successfully for ${currentQuarter}! Thank you for your input.`);
        setHasSubmittedFeedback(true);
        setHasExistingDraft(false); // Draft no longer exists after submission
        
        // Reset form after 2 seconds (or keep it read-only)
        setTimeout(() => {
          setSuccess('');
        }, 3000);
      }
    } catch (err) {
      //console.error('Submit error:', err);
      if (err.response?.status === 400 && 
          err.response?.data?.message?.includes('already submitted')) {
        setHasSubmittedFeedback(true);
        setError(`You have already submitted feedback for ${currentQuarter}.`);
      } else {
        setError(err.response?.data?.message || 'Failed to submit feedback');
      }
    } finally {
      setLoading(false);
    }
  };

  const ratingOptions = [
    { value: '1', label: 'Very Dissatisfied' },
    { value: '2', label: 'Dissatisfied' },
    { value: '3', label: 'Neutral' },
    { value: '4', label: 'Satisfied' },
    { value: '5', label: 'Very Satisfied' }
  ];

  const RatingQuestion = ({ label, field, value, description }) => (
    <Box sx={{ mb: 4 }}>
      <FormControl component="fieldset" fullWidth disabled={hasSubmittedFeedback}>
        <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
          {label} <span style={{ color: 'red' }}>*</span>
        </FormLabel>
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, ml: 0 }}>
            {description}
          </Typography>
        )}
        <RadioGroup
          row
          value={value}
          onChange={handleRadioChange(field)}
          sx={{ 
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1
          }}
        >
          {ratingOptions.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              control={<Radio disabled={hasSubmittedFeedback} />}
              label={option.label}
              sx={{
                border: '1px solid',
                borderColor: value === option.value ? 'primary.main' : 'divider',
                borderRadius: 1,
                px: 2,
                py: 1,
                m: 0,
                flex: { xs: '1 1 100%', sm: '1 1 45%', md: '0 1 auto' },
                bgcolor: value === option.value ? 'primary.50' : 'transparent',
                '&:hover': {
                  bgcolor: hasSubmittedFeedback ? 'transparent' : 'action.hover'
                }
              }}
            />
          ))}
        </RadioGroup>
      </FormControl>
    </Box>
  );

  if (fetchingDraft) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading your feedback form...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ py: 4, width: '100%' }}>
      {/* Draft Confirmation Dialog */}
      <Dialog 
        open={showDraftDialog && !hasSubmittedFeedback} 
        onClose={() => setShowDraftDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          📝 Unsaved Draft Found
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            We found an unsaved draft from <strong>{draftToLoad?.feedbackPeriod}</strong>.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Last saved: {draftToLoad ? new Date(draftToLoad.updatedAt).toLocaleString() : ''}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Would you like to continue with this draft or start fresh?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={discardDraft} variant="outlined" color="error">
            Discard & Start Fresh
          </Button>
          <Button onClick={loadDraft} variant="contained" color="primary" autoFocus>
            Load Draft
          </Button>
        </DialogActions>
      </Dialog>

          <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 2, border: '2px solid', borderColor: 'grey.300', boxShadow: 4 }}>
        <Box sx={{ mb: 4, pb: 3, borderBottom: '2px solid', borderColor: 'primary.main' }}>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Give Feedback
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Your feedback is confidential and helps us create a better workplace. Please take a few minutes to share your honest thoughts.
          </Typography>
          
          {hasSubmittedFeedback && (
            <Alert severity="success" sx={{ mt: 2 }}>
              ✅ You have already submitted feedback for {currentQuarter}. Thank you for your participation!
            </Alert>
          )}
          
          {hasExistingDraft && !showDraftDialog && !hasSubmittedFeedback && (
            <Alert severity="info" sx={{ mt: 2 }}>
              📝 You have an unsaved draft for {currentQuarter}. Changes will be saved automatically as you work.
            </Alert>
          )}
        </Box>

        {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Employee ID"
                value={employeeId || ''}
                disabled
                fullWidth
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Feedback Period"
                value={formData.feedbackPeriod}
                onChange={handleTextChange('feedbackPeriod')}
                disabled={hasSubmittedFeedback}
                fullWidth
                required
                placeholder="e.g., Q1 2025"
                variant="outlined"
                helperText={hasSubmittedFeedback ? "Feedback already submitted for this period" : ""}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          {hasSubmittedFeedback ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h5" color="success.main" gutterBottom>
                ✅ Feedback Submitted Successfully
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Thank you for submitting your feedback for {currentQuarter}.<br />
                Your responses have been recorded and will be used to improve our workplace.
              </Typography>
              <Box sx={{ mt: 4 }}>
                <Button 
                  variant="outlined" 
                  onClick={() => window.location.reload()}
                  sx={{ mt: 2 }}
                >
                  Refresh Page
                </Button>
              </Box>
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" gutterBottom fontWeight="600" color="text.primary" sx={{ mb: 3 }}>
                  Section 1: Work Experience Assessment
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                  Please rate your experience in the following areas (all fields required):
                </Typography>

                <Card variant="outlined" sx={{ mb: 3, p: 3 }}>
                  <RatingQuestion
                    label="1. Employee Engagement"
                    field="engagementScore"
                    value={formData.engagementScore}
                    description="How engaged and motivated do you feel in your current role?"
                  />

                  <Divider sx={{ my: 3 }} />

                  <RatingQuestion
                    label="2. Work-Life Balance"
                    field="workLifeBalance"
                    value={formData.workLifeBalance}
                    description="How well are you able to balance your professional and personal commitments?"
                  />

                  <Divider sx={{ my: 3 }} />
                  
                  <RatingQuestion
                    label="3. Manager Satisfaction Score"
                    field="managerSatificationScore"
                    value={formData.managerSatificationScore}
                    description="How would you rate your manager's support, communication, and ability to help you succeed in your role?"
                  />

                  <Divider sx={{ my: 3 }} />

                  <RatingQuestion
                    label="4. Management Support"
                    field="managementSupport"
                    value={formData.managementSupport}
                    description="How would you rate the support and guidance you receive from your manager?"
                  />

                  <Divider sx={{ my: 3 }} />

                  <RatingQuestion
                    label="5. Career Development"
                    field="careerGrowth"
                    value={formData.careerGrowth}
                    description="Do you see clear opportunities for professional growth and advancement?"
                  />

                  <Divider sx={{ my: 3 }} />

                  <RatingQuestion
                    label="6. Compensation & Benefits"
                    field="compensation"
                    value={formData.compensation}
                    description="How satisfied are you with your total compensation package?"
                  />

                  <Divider sx={{ my: 3 }} />

                  <RatingQuestion
                    label="7. Work Environment"
                    field="workEnvironment"
                    value={formData.workEnvironment}
                    description="How would you rate the overall work environment and company culture?"
                  />

                  <Divider sx={{ my: 3 }} />

                  <RatingQuestion
                    label="8. Team Collaboration"
                    field="teamCollaboration"
                    value={formData.teamCollaboration}
                    description="How effectively does your team collaborate and communicate?"
                  />

                  <Divider sx={{ my: 3 }} />

                  <RatingQuestion
                    label="9. Learning & Development"
                    field="learningDevelopment"
                    value={formData.learningDevelopment}
                    description="Are there sufficient opportunities for learning and skill enhancement?"
                  />

                  <Divider sx={{ my: 3 }} />

                  <RatingQuestion
                    label="10. Recognition & Appreciation"
                    field="recognition"
                    value={formData.recognition}
                    description="Do you feel adequately recognized and appreciated for your contributions?"
                  />

                  <Divider sx={{ my: 3 }} />

                  <RatingQuestion
                    label="11. Overall Job Satisfaction"
                    field="overallSatisfaction"
                    value={formData.overallSatisfaction}
                    description="How satisfied are you with your job overall?"
                  />
                </Card>
              </Box>

              <Divider sx={{ my: 4 }} />

              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" gutterBottom fontWeight="600" color="text.primary" sx={{ mb: 3 }}>
                  Section 2: Qualitative Feedback
                </Typography>

                <Card variant="outlined" sx={{ p: 3 }}>
                  <TextField
                    label="What aspects of your work experience do you value most? *"
                    fullWidth
                    multiline
                    rows={5}
                    value={formData.likeMost}
                    onChange={handleTextChange('likeMost')}
                    required
                    disabled={hasSubmittedFeedback}
                    sx={{ mb: 3 }}
                    placeholder="Please share specific examples of positive experiences, practices, or aspects of your role that you find most valuable..."
                    variant="outlined"
                  />

                  <TextField
                    label="What areas do you believe need improvement? *"
                    fullWidth
                    multiline
                    rows={5}
                    value={formData.improvements}
                    onChange={handleTextChange('improvements')}
                    required
                    disabled={hasSubmittedFeedback}
                    sx={{ mb: 3 }}
                    placeholder="Please provide constructive suggestions for improvements in processes, policies, or practices..."
                    variant="outlined"
                  />

                  <TextField
                    label="Additional Comments"
                    fullWidth
                    multiline
                    rows={4}
                    value={formData.additionalComments}
                    onChange={handleTextChange('additionalComments')}
                    disabled={hasSubmittedFeedback}
                    sx={{ mb: 3 }}
                    placeholder="Any other feedback, concerns, or suggestions you would like to share..."
                    variant="outlined"
                  />

                  <FormControl fullWidth variant="outlined" required disabled={hasSubmittedFeedback}>
                    <InputLabel>Would you recommend this organization as a great place to work? *</InputLabel>
                    <Select
                      value={formData.wouldRecommend}
                      onChange={handleTextChange('wouldRecommend')}
                      disabled={hasSubmittedFeedback}
                      label="Would you recommend this organization as a great place to work? *"
                      required
                    >
                      <MenuItem value="Yes">Yes, definitely</MenuItem>
                      <MenuItem value="Maybe">Perhaps, with some reservations</MenuItem>
                      <MenuItem value="No">No, not at this time</MenuItem>
                    </Select>
                  </FormControl>
                </Card>
              </Box>

              <Divider sx={{ my: 4 }} />

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
                <Button
                  variant="outlined"
                  onClick={handleSaveDraft}
                  disabled={loading || !employeeId || !companyId || hasSubmittedFeedback}
                  size="large"
                  sx={{ minWidth: 140 }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Save Draft'}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading || !employeeId || !companyId || hasSubmittedFeedback}
                  size="large"
                  sx={{ minWidth: 180 }}
                >
                  Submit Feedback
                </Button>
              </Box>
            </>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3, textAlign: 'center' }}>
            Your responses are confidential and will be used solely for organizational improvement purposes.
          </Typography>
        </form>
      </Paper>
    </Container>
  );
};

export default EmployeeFeedbackForm;